import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Avatar, Tag, Button, Spin, Empty, Badge } from 'antd';
import {
  ClockCircleOutlined, CheckCircleOutlined, WarningOutlined,
  FireOutlined, AimOutlined, ArrowRightOutlined, TeamOutlined,
  BellOutlined, ThunderboltOutlined, CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import AppLayout from '../components/AppLayout';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

dayjs.extend(relativeTime);

// ── Shared ────────────────────────────────────────────────────────────────────
function SectionCard({ title, icon, color, count, children, extra }) {
  return (
    <Card
      variant="outlined"
      style={{ borderColor: count > 0 ? color + '66' : '#e8e8e8', marginBottom: 16,
        background: count > 0 ? color + '08' : '#fff' }}
      title={
        <span style={{ fontWeight: 600, fontSize: 14, color: count > 0 ? color : '#0a0a0a' }}>
          {React.cloneElement(icon, { style: { marginRight: 8 } })}
          {title}
          {count > 0 && <Badge count={count} style={{ marginLeft: 10, background: color }} />}
        </span>
      }
      extra={extra}
    >
      {children}
    </Card>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0', color: '#bbb', fontSize: 13 }}>
      <CheckCircleOutlined style={{ marginRight: 6 }} />{text}
    </div>
  );
}

const PLANNER_TYPE = {
  goal:     { label: 'Goal',     color: '#722ed1' },
  task:     { label: 'Task',     color: '#0958d9' },
  homework: { label: 'Homework', color: '#d46b08' },
};

// Items planned/shared with this user — shown to both tutors and students.
function PlannerSection({ items, navigate }) {
  if (!items.length) return null;
  return (
    <SectionCard
      title="Planned for you"
      icon={<CalendarOutlined />}
      color="#13c2c2"
      count={items.length}
      extra={<Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/schedule')}>Open Planner <ArrowRightOutlined /></Button>}
    >
      {items
        .slice()
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
        .map(n => {
          const t    = PLANNER_TYPE[n.item_type] || PLANNER_TYPE.task;
          const days = dayjs(n.due_date).startOf('day').diff(dayjs().startOf('day'), 'day');
          const due  = days < 0 ? `${-days}d overdue` : days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `in ${days}d`;
          const col  = days < 0 ? '#cf1322' : days <= 1 ? '#fa8c16' : '#888';
          return (
            <div key={n.key} onClick={() => navigate('/schedule')}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}>
              <Tag style={{ fontSize: 10, color: t.color, borderColor: t.color, background: 'transparent', flexShrink: 0 }}>{t.label}</Tag>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
                <div style={{ fontSize: 11, color: '#888' }}>From {n.owner_name}</div>
              </div>
              <span style={{ fontSize: 11, color: col, fontWeight: 600, flexShrink: 0 }}>{due}</span>
              <ArrowRightOutlined style={{ color: '#ccc', fontSize: 11 }} />
            </div>
          );
        })}
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function TutorTodo({ user, navigate }) {
  const [data, setData]       = useState(null);
  const [notifs, setNotifs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dash_dismissed') || '[]')); } catch { return new Set(); }
  });

  useEffect(() => {
    Promise.all([
      api.get('/api/analytics/tutor'),
      api.get('/api/dashboard/notifications'),
    ]).then(([ana, not]) => {
      setData(ana.data);
      setNotifs((not.data.items || []).filter(n => !dismissed.has(n.key)));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []); // runs fresh on every page visit (component remounts on navigation)

  const dismiss = (key) => {
    const next = new Set([...dismissed, key]);
    setDismissed(next);
    localStorage.setItem('dash_dismissed', JSON.stringify([...next]));
    setNotifs(prev => prev.filter(n => n.key !== key));
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  const { students = [], unassigned = [] } = data || {};
  const pending    = students.filter(s => s.pending_review > 0).sort((a, b) => new Date(a.last_activity) - new Date(b.last_activity));
  const withExam   = students.filter(s => s.exam_date).sort((a, b) => new Date(a.exam_date) - new Date(b.exam_date)).slice(0, 6);
  const newSubs    = notifs.filter(n => n.type === 'new_subscription');
  const planner    = notifs.filter(n => n.type === 'planner_shared');

  return (
    <div>
      {/* 0. Planner */}
      <PlannerSection items={planner} navigate={navigate} />

      {/* 1. Pending Reviews */}
      <SectionCard
        title="Submissions to review"
        icon={<ClockCircleOutlined />}
        color="#fa8c16"
        count={pending.length}
        extra={pending.length > 0 && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/stage-gate')}>Review all <ArrowRightOutlined /></Button>}
      >
        {pending.length === 0
          ? <EmptyState text="No submissions waiting — you're all clear." />
          : pending.map(s => (
            <div key={s.assignment_id}
              onClick={() => navigate(`/stage-gate?studentId=${s.student_id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}>
              <Avatar size={34} src={s.student_avatar} style={{ background: '#fa8c16', fontSize: 12, flexShrink: 0 }}>
                {!s.student_avatar && s.student_name?.[0]}
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.student_name}</div>
                <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.current_stage} · {s.subject_name} {s.level}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <Tag color="orange">{s.pending_review} pending</Tag>
                {s.last_activity && <span style={{ fontSize: 11, color: '#aaa' }}>{dayjs(s.last_activity).fromNow()}</span>}
                <ArrowRightOutlined style={{ color: '#ccc', fontSize: 11 }} />
              </div>
            </div>
          ))
        }
      </SectionCard>

      {/* 2. Not assigned */}
      <SectionCard
        title="Students not yet assigned a Roadmap"
        icon={<WarningOutlined />}
        color="#d46b08"
        count={unassigned.length}
        extra={unassigned.length > 0 && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/stage-gate')}>Go to Roadmap <ArrowRightOutlined /></Button>}
      >
        {unassigned.length === 0
          ? <EmptyState text="All subscribed students have been assigned." />
          : unassigned.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
              borderBottom: i < unassigned.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
              <Avatar size={34} src={s.student_avatar} style={{ background: '#fac858', fontSize: 12, flexShrink: 0 }}>
                {!s.student_avatar && s.student_name?.[0]}
              </Avatar>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.student_name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{s.subject_name} {s.level}</div>
              </div>
              <Button size="small" type="primary" style={{ background: '#0a0a0a', border: 'none' }}
                onClick={() => navigate(`/stage-gate?studentId=${s.user_id}`)}>
                Assign now
              </Button>
            </div>
          ))
        }
      </SectionCard>

      {/* 3. Upcoming exams */}
      <SectionCard
        title="Upcoming exam dates"
        icon={<FireOutlined />}
        color="#ff4d4f"
        count={withExam.filter(s => dayjs(s.exam_date).diff(dayjs(), 'day') <= 14).length}
      >
        {withExam.length === 0
          ? <EmptyState text="No upcoming exam dates set." />
          : <Row gutter={[12, 12]}>
              {withExam.map(s => {
                const days = dayjs(s.exam_date).diff(dayjs(), 'day');
                const col  = days <= 7 ? '#ff4d4f' : days <= 14 ? '#fa8c16' : '#0a0a0a';
                return (
                  <Col xs={24} sm={12} key={s.assignment_id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', border: `1px solid ${col}33`, borderLeft: `4px solid ${col}`,
                      borderRadius: 8, background: `${col}06` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar size={28} src={s.student_avatar} style={{ background: col, fontSize: 11 }}>
                          {!s.student_avatar && s.student_name?.[0]}
                        </Avatar>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{s.student_name}</div>
                          <div style={{ fontSize: 11, color: '#aaa' }}>{dayjs(s.exam_date).format('D MMM YYYY')}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: col, lineHeight: 1 }}>{days}</div>
                        <div style={{ fontSize: 10, color: '#aaa' }}>days</div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
        }
      </SectionCard>

      {/* 4. New subscriptions */}
      {newSubs.length > 0 && (
        <SectionCard
          title="New students"
          icon={<TeamOutlined />}
          color="#1677ff"
          count={newSubs.length}
        >
          {newSubs.map(n => (
            <div key={n.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '9px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
              <span>
                <strong>{n.student_name}</strong> subscribed to <strong>{n.subject_name} {n.level ? `(${n.level})` : ''}</strong>
                <span style={{ color: '#aaa', marginLeft: 8 }}>{dayjs(n.created_at).fromNow()}</span>
              </span>
              <Button type="text" size="small" style={{ color: '#ccc', fontSize: 11 }} onClick={() => dismiss(n.key)}>Dismiss</Button>
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function StudentTodo({ user, navigate }) {
  const [assignments, setAssignments] = useState([]);
  const [notifs, setNotifs]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dash_dismissed') || '[]')); } catch { return new Set(); }
  });

  useEffect(() => {
    Promise.all([
      api.get(`/api/gate/assignments/student/${user.userId}`),
      api.get('/api/dashboard/notifications'),
    ]).then(([aRes, nRes]) => {
      setAssignments(aRes.data || []);
      setNotifs((nRes.data.items || []).filter(n => !dismissed.has(n.key)));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const dismiss = (key) => {
    const next = new Set([...dismissed, key]);
    setDismissed(next);
    localStorage.setItem('dash_dismissed', JSON.stringify([...next]));
    setNotifs(prev => prev.filter(n => n.key !== key));
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;

  const inProgress   = assignments.filter(a => a.completed_stages < a.total_stages);
  const withExam     = assignments.filter(a => a.exam_date).sort((a, b) => new Date(a.exam_date) - new Date(b.exam_date));
  const rejected     = notifs.filter(n => n.type === 'gate_rejected');
  const approved     = notifs.filter(n => n.type === 'gate_approved');
  const newAssign    = notifs.filter(n => n.type === 'new_assignment');
  const noTemplate   = notifs.filter(n => n.type === 'no_template');
  const expiring     = notifs.filter(n => n.type === 'expiring_soon');
  const planner      = notifs.filter(n => n.type === 'planner_shared');

  return (
    <div>
      {/* 0. Planner */}
      <PlannerSection items={planner} navigate={navigate} />

      {/* 1. Action required */}
      <SectionCard
        title="Needs your attention"
        icon={<ThunderboltOutlined />}
        color="#ff4d4f"
        count={rejected.length}
        extra={<Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/stage-gate')}>Open Roadmap <ArrowRightOutlined /></Button>}
      >
        {rejected.length === 0 && inProgress.length === 0
          ? <EmptyState text="Nothing to do right now." />
          : <>
              {rejected.map(n => (
                <div key={n.key} onClick={() => navigate('/stage-gate')}
                  style={{ padding: '12px 14px', marginBottom: 8, background: '#fff2f0',
                    border: '1px solid #ffccc7', borderRadius: 8, cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#cf1322' }}>
                    <WarningOutlined style={{ marginRight: 6 }} />Needs rework: {n.stage_name}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{n.template_title}</div>
                  {n.tutor_feedback && (
                    <div style={{ fontSize: 12, color: '#555', marginTop: 6, fontStyle: 'italic', borderLeft: '3px solid #ffccc7', paddingLeft: 8 }}>
                      "{n.tutor_feedback}"
                    </div>
                  )}
                </div>
              ))}
              {inProgress.map(a => (
                <div key={a.assignment_id} onClick={() => navigate('/stage-gate')}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                    borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}>
                  <AimOutlined style={{ fontSize: 20, color: '#5470c6', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{a.template_title}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{a.tutor_name} · {a.completed_stages}/{a.total_stages} stages done</div>
                  </div>
                  <ArrowRightOutlined style={{ color: '#ccc' }} />
                </div>
              ))}
            </>
        }
      </SectionCard>

      {/* 2. Upcoming exams */}
      <SectionCard
        title="Upcoming exam dates"
        icon={<FireOutlined />}
        color="#ff4d4f"
        count={withExam.filter(a => dayjs(a.exam_date).diff(dayjs(), 'day') <= 14).length}
      >
        {withExam.length === 0
          ? <EmptyState text="No exam dates set by your tutors yet." />
          : <Row gutter={[12, 12]}>
              {withExam.map(a => {
                const days = dayjs(a.exam_date).diff(dayjs(), 'day');
                const col  = days <= 7 ? '#ff4d4f' : days <= 14 ? '#fa8c16' : '#5470c6';
                return (
                  <Col xs={24} sm={12} key={a.assignment_id}>
                    <div style={{ padding: '10px 14px', border: `1px solid ${col}33`,
                      borderLeft: `4px solid ${col}`, borderRadius: 8, background: `${col}06`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{a.template_title}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{a.tutor_name}</div>
                        <div style={{ fontSize: 11, color: '#aaa' }}>{dayjs(a.exam_date).format('D MMM YYYY')}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: col, lineHeight: 1 }}>{days}</div>
                        <div style={{ fontSize: 10, color: '#aaa' }}>days left</div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
        }
      </SectionCard>

      {/* 3. Updates */}
      {(approved.length > 0 || newAssign.length > 0 || noTemplate.length > 0 || expiring.length > 0) && (
        <SectionCard title="Updates" icon={<BellOutlined />} color="#1677ff" count={newAssign.length + approved.length}>
          {approved.map(n => (
            <div key={n.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #f5f5f5' }}>
              <div>
                <div style={{ fontSize: 13 }}><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />Stage cleared: <strong>{n.stage_name}</strong></div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{n.template_title} · {dayjs(n.completed_at).fromNow()}</div>
              </div>
              <Button type="text" size="small" style={{ color: '#ccc' }} onClick={() => dismiss(n.key)}>Dismiss</Button>
            </div>
          ))}
          {newAssign.map(n => (
            <div key={n.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #f5f5f5' }}>
              <div>
                <div style={{ fontSize: 13 }}><AimOutlined style={{ color: '#5470c6', marginRight: 6 }} /><strong>{n.tutor_name}</strong> assigned you <strong>{n.template_title}</strong></div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{dayjs(n.assigned_at).fromNow()}</div>
              </div>
              <Button type="text" size="small" style={{ color: '#ccc' }} onClick={() => dismiss(n.key)}>Dismiss</Button>
            </div>
          ))}
          {noTemplate.map(n => (
            <div key={n.key} style={{ padding: '9px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13, color: '#888' }}>
              <WarningOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
              Subscribed to <strong>{n.subject_name}</strong> with <strong>{n.tutor_name}</strong> — waiting for tutor to assign work
            </div>
          ))}
          {expiring.map(n => (
            <div key={n.key} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
              <span style={{ color: '#888' }}>
                <WarningOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
                Subscription to <strong>{n.subject_name}</strong> expires <strong>{dayjs(n.end_date).format('D MMM YYYY')}</strong>
              </span>
              <Button type="text" size="small" style={{ color: '#ccc' }} onClick={() => dismiss(n.key)}>Dismiss</Button>
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user }  = useUser();
  const navigate  = useNavigate();
  const isTutor   = user.role === 'tutor' || user.role === 'admin';

  return (
    <AppLayout>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>{isTutor ? 'Your to-do list.' : `Your to-do list, ${user.name || user.username}.`}</p>
      </div>
      {isTutor
        ? <TutorTodo user={user} navigate={navigate} />
        : <StudentTodo user={user} navigate={navigate} />
      }
    </AppLayout>
  );
}
